import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CoordinateSpace, ImageSize, ImageVisualizerBox } from './image-visualizer.types';
import { MatIcon, MatIconModule } from '@angular/material/icon';

interface RenderedBox extends ImageVisualizerBox {
  leftPct: number;
  topPct: number;
  widthPct: number;
  heightPct: number;
  area: number;
  zIndex: number;
}

@Component({
  selector: 'app-image-visualizer',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, MatIconModule, MatIcon],
  templateUrl: './image-visualizer.component.html',
  styleUrl: './image-visualizer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageVisualizerComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() imageSrc = '';
  @Input() imageAlt = 'Image';
  @Input() boxes: ImageVisualizerBox[] = [];
  @Input() coordinateSpace: CoordinateSpace = 'image';
  @Input() imageSize?: ImageSize;

  @Input() selectedBoxId: string | number | null = null;
  @Input() highlightedBoxIds: Array<string | number> = [];
  @Input() showLabels = false;
  @Input() showZoomControls = true;
  @Input() showInfoCard = true;
  @Input() showBoxTooltip = true;
  @Input() enableWheelZoom = true;
  @Input() scrollOnFocus = false;
  @Input() fitMode: 'auto' | 'contain' | 'width' | 'height' = 'auto';

  @Input() zoom = 1;
  @Input() minZoom = 1;
  @Input() maxZoom = 6;
  @Input() zoomStep = 0.2;

  @Output() boxClick = new EventEmitter<ImageVisualizerBox>();
  @Output() boxHover = new EventEmitter<ImageVisualizerBox | null>();
  @Output() zoomChange = new EventEmitter<number>();
  @Output() boxDismiss = new EventEmitter<void>();
  @Output() imageLoaded = new EventEmitter<ImageSize>();

  @ViewChild('viewport', { static: true }) viewportRef!: ElementRef<HTMLDivElement>;
  @ViewChild('image') imageRef?: ElementRef<HTMLImageElement>;
  @ViewChild('infoCard')
  set infoCardRef(ref: ElementRef<HTMLDivElement> | undefined) {
    this.infoCardObserver?.disconnect();
    if (!ref) return;

    this.infoCardObserver = new ResizeObserver(() => this.updateInfoCardSize());
    this.infoCardObserver.observe(ref.nativeElement);
    queueMicrotask(() => this.updateInfoCardSize());
  }

  renderedBoxes: RenderedBox[] = [];
  stageWidth = 0;
  stageHeight = 0;
  currentZoom = 1;
  isDockedInfo = false;
  forceDockedInfo = false;

  private naturalSize?: ImageSize;
  private baseWidth = 0;
  private baseHeight = 0;
  private resizeObserver?: ResizeObserver;
  private infoCardObserver?: ResizeObserver;
  private warnedMissingImageSize = false;
  private pinchActive = false;
  private pinchStartDistance = 0;
  private pinchStartZoom = 1;
  isDragging = false;
  private dragPending = false;
  private dragMoved = false;
  private readonly dragThreshold = 4;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragStartScrollLeft = 0;
  private dragStartScrollTop = 0;
  private infoCardWidth = 220;
  private infoCardHeight = 220;

  constructor(
    private cdr: ChangeDetectorRef,
    private hostRef: ElementRef<HTMLElement>,
  ) {}

  ngAfterViewInit(): void {
    this.setupResizeObserver();
    this.syncImageSize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['boxes'] || changes['coordinateSpace'] || changes['imageSize']) {
      this.renderedBoxes = this.buildRenderedBoxes();
    }

    if (changes['zoom'] || changes['minZoom'] || changes['maxZoom']) {
      this.setZoom(this.zoom, false);
    }

    if (changes['imageSrc'] && !changes['imageSrc'].firstChange) {
      queueMicrotask(() => this.syncImageSize());
    }

    if ((changes['selectedBoxId'] || changes['highlightedBoxIds']) && this.scrollOnFocus) {
      queueMicrotask(() => this.scrollFocusedIntoView());
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.infoCardObserver?.disconnect();
  }

  onImageLoad(): void {
    this.syncImageSize();
  }

  onWheel(event: WheelEvent): void {
    if (!this.enableWheelZoom) return;
    if (!event.ctrlKey && !event.metaKey && !event.shiftKey) return;

    event.preventDefault();
    const direction = event.deltaY < 0 ? 1 : -1;
    this.setZoom(this.currentZoom + direction * this.zoomStep, true);
  }

  onTouchStart(event: TouchEvent): void {
    if (event.touches.length !== 2) return;

    this.pinchActive = true;
    this.pinchStartDistance = this.getTouchDistance(event.touches[0], event.touches[1]);
    this.pinchStartZoom = this.currentZoom;
  }

  onTouchMove(event: TouchEvent): void {
    if (!this.pinchActive || event.touches.length !== 2) return;

    event.preventDefault();
    const distance = this.getTouchDistance(event.touches[0], event.touches[1]);
    if (!distance) return;

    const scale = distance / this.pinchStartDistance;
    const center = this.getTouchCenter(event.touches[0], event.touches[1]);
    this.setZoomAt(this.pinchStartZoom * scale, center.x, center.y, true);
  }

  onTouchEnd(_event: TouchEvent): void {
    this.pinchActive = false;
  }

  onMouseDown(event: MouseEvent): void {
    if (event.button !== 0) return;
    if (this.currentZoom <= 1) return;

    event.preventDefault();
    const viewport = this.viewportRef.nativeElement;
    this.dragPending = true;
    this.dragMoved = false;
    this.isDragging = false;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragStartScrollLeft = viewport.scrollLeft;
    this.dragStartScrollTop = viewport.scrollTop;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.dragPending && !this.isDragging) return;

    event.preventDefault();
    const viewport = this.viewportRef.nativeElement;
    const deltaX = event.clientX - this.dragStartX;
    const deltaY = event.clientY - this.dragStartY;

    if (!this.isDragging) {
      const distance = Math.hypot(deltaX, deltaY);
      if (distance < this.dragThreshold) return;
      this.isDragging = true;
      this.dragMoved = true;
    }

    viewport.scrollLeft = this.dragStartScrollLeft - deltaX;
    viewport.scrollTop = this.dragStartScrollTop - deltaY;
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    if (!this.dragPending && !this.isDragging) return;
    this.dragPending = false;
    this.isDragging = false;
    if (this.dragMoved) {
      setTimeout(() => {
        this.dragMoved = false;
      }, 0);
    }
  }

  @HostListener('window:blur')
  onWindowBlur(): void {
    this.resetDragState();
  }

  @HostListener('document:mouseleave')
  onDocumentLeave(): void {
    this.resetDragState();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target) return;
    if (this.hostRef.nativeElement.contains(target)) return;
    this.boxDismiss.emit();
  }

  zoomIn(): void {
    this.setZoom(this.currentZoom + this.zoomStep, true);
  }

  zoomOut(): void {
    this.setZoom(this.currentZoom - this.zoomStep, true);
  }

  resetZoom(): void {
    this.setZoom(1, true);
  }

  onBoxClick(box: ImageVisualizerBox): void {
    if (this.consumeDragClick()) return;
    this.boxClick.emit(box);
  }

  onViewportClick(event: MouseEvent): void {
    if (this.consumeDragClick()) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('.box')) return;
    if (target?.closest('.info-card')) return;
    this.boxDismiss.emit();
  }

  onBoxHover(box: ImageVisualizerBox | null): void {
    this.boxHover.emit(box);
  }

  get focusedBox(): RenderedBox | null {
    if (this.selectedBoxId === null) return null;
    return this.renderedBoxes.find((box) =>
      box.id === this.selectedBoxId || String(box.id) === String(this.selectedBoxId)
    ) ?? null;
  }

  isFocused(box: ImageVisualizerBox): boolean {
    return this.selectedBoxId !== null && box.id === this.selectedBoxId;
  }

  isHighlighted(box: ImageVisualizerBox): boolean {
    return this.highlightedBoxIds.includes(box.id);
  }

  getBoxStyle(box: RenderedBox): Record<string, string | number> {
    return {
      left: `${box.leftPct}%`,
      top: `${box.topPct}%`,
      width: `${box.widthPct}%`,
      height: `${box.heightPct}%`,
      zIndex: box.zIndex,
      borderStyle: box.borderStyle ?? 'solid',
      '--box-color': box.color ?? '#00e5ff',
    } as Record<string, string | number>;
  }

  getTooltip(box: ImageVisualizerBox): string {
    return box.tooltip || box.label || String(box.id);
  }

  getMetadataEntries(box: ImageVisualizerBox): Array<{ key: string; value: string }> {
    const metadata = (box.metadata ?? {}) as Record<string, unknown>;
    return Object.entries(metadata)
      .filter(([key]) => key !== 'bounding_box')
      .map(([key, value]) => ({
        key,
        value: this.formatMetadataValue(value),
      }))
      .filter((entry) => entry.value.length > 0);
  }

  trackByMetadata(_index: number, entry: { key: string; value: string }): string {
    return entry.key;
  }

  getInfoStyle(box: RenderedBox): Record<string, string> {
    const leftPx = (box.leftPct / 100) * this.stageWidth;
    const topPx = (box.topPct / 100) * this.stageHeight;
    const widthPx = (box.widthPct / 100) * this.stageWidth;
    const heightPx = (box.heightPct / 100) * this.stageHeight;

    const cardWidth = this.infoCardWidth || 260;
    const cardHeight = this.infoCardHeight || 220;
    const offset = 6;
    const safeGap = 8;
    const gapRight = offset;
    const gapLeft = offset + safeGap;
    const gapTop = offset + safeGap;
    const gapBottom = offset + safeGap;
    const stagePad = 4;

    const boxRight = leftPx + widthPx;
    const boxBottom = topPx + heightPx;
    const stageLeft = stagePad;
    const stageTop = stagePad;
    const stageRight = this.stageWidth - stagePad;
    const stageBottom = this.stageHeight - stagePad;

    const maxWidth = stageRight - stageLeft;
    const maxHeight = stageBottom - stageTop;

    const clampY = (y: number, height: number) =>
      this.clamp(y, stageTop, Math.max(stageTop, stageBottom - height));
    const clampX = (x: number, width: number) =>
      this.clamp(x, stageLeft, Math.max(stageLeft, stageRight - width));

    const tryPlace = (
      minWidth: number,
      minHeight: number,
      gRight: number,
      gLeft: number,
      gTop: number,
      gBottom: number,
    ): { x: number; y: number; width: number; height: number } | null => {
      const topSpace = topPx - gTop - stageTop;
      const bottomSpace = stageBottom - (boxBottom + gBottom);
      const leftSpace = leftPx - gLeft - stageLeft;
      const rightSpace = stageRight - (boxRight + gRight);

      // Top
      if (topSpace >= minHeight && maxWidth >= minWidth) {
        const width = Math.min(cardWidth, maxWidth);
        const height = Math.min(cardHeight, topSpace);
        if (width >= minWidth && height >= minHeight) {
          const x = clampX(leftPx, width);
          const y = topPx - gTop - height;
          return { x, y, width, height };
        }
      }

      // Right
      if (rightSpace >= minWidth && maxHeight >= minHeight) {
        const width = Math.min(cardWidth, rightSpace);
        const height = Math.min(cardHeight, maxHeight);
        if (width >= minWidth && height >= minHeight) {
          const x = boxRight + gRight;
          const y = clampY(topPx, height);
          return { x, y, width, height };
        }
      }

      // Bottom
      if (bottomSpace >= minHeight && maxWidth >= minWidth) {
        const width = Math.min(cardWidth, maxWidth);
        const height = Math.min(cardHeight, bottomSpace);
        if (width >= minWidth && height >= minHeight) {
          const x = clampX(leftPx, width);
          const y = boxBottom + gBottom;
          return { x, y, width, height };
        }
      }

      // Left
      if (leftSpace >= minWidth && maxHeight >= minHeight) {
        const width = Math.min(cardWidth, leftSpace);
        const height = Math.min(cardHeight, maxHeight);
        if (width >= minWidth && height >= minHeight) {
          const x = leftPx - gLeft - width;
          const y = clampY(topPx, height);
          return { x, y, width, height };
        }
      }

      return null;
    };

    const primary = tryPlace(180, 96, gapRight, gapLeft, gapTop, gapBottom);
    if (primary) {
      this.setDocked(false);
      return {
        left: `${primary.x}px`,
        top: `${primary.y}px`,
        width: `${primary.width}px`,
        maxHeight: `${primary.height}px`,
      };
    }

    const secondary = tryPlace(120, 72, 6, 6, 6, 6);
    if (secondary) {
      this.setDocked(false);
      return {
        left: `${secondary.x}px`,
        top: `${secondary.y}px`,
        width: `${secondary.width}px`,
        maxHeight: `${secondary.height}px`,
      };
    }

    this.setDocked(true);
    return {
      left: `${clampX(leftPx, cardWidth)}px`,
      top: `${clampY(topPx, cardHeight)}px`,
      width: `${Math.min(cardWidth, maxWidth)}px`,
      maxHeight: `${Math.min(cardHeight, maxHeight)}px`,
    };
  }

  trackByBox(_index: number, box: RenderedBox): string | number {
    return box.id;
  }

  private setZoom(value: number, emit: boolean): void {
    const clamped = Math.min(Math.max(value, this.minZoom), this.maxZoom);
    if (clamped === this.currentZoom) return;

    this.currentZoom = clamped;
    this.updateStageSize();

    if (emit) {
      this.zoomChange.emit(this.currentZoom);
    }

    this.cdr.markForCheck();
  }

  private setZoomAt(value: number, centerX: number, centerY: number, emit: boolean): void {
    const viewport = this.viewportRef.nativeElement;
    if (!this.baseWidth || !this.baseHeight) {
      this.setZoom(value, emit);
      return;
    }

    const clamped = Math.min(Math.max(value, this.minZoom), this.maxZoom);
    if (clamped === this.currentZoom) return;

    const prevZoom = this.currentZoom;
    const prevScrollLeft = viewport.scrollLeft;
    const prevScrollTop = viewport.scrollTop;
    const contentX = (prevScrollLeft + centerX) / prevZoom;
    const contentY = (prevScrollTop + centerY) / prevZoom;

    this.currentZoom = clamped;
    this.updateStageSize();

    const maxScrollLeft = Math.max(0, this.stageWidth - viewport.clientWidth);
    const maxScrollTop = Math.max(0, this.stageHeight - viewport.clientHeight);
    const nextScrollLeft = this.clamp(contentX * this.currentZoom - centerX, 0, maxScrollLeft);
    const nextScrollTop = this.clamp(contentY * this.currentZoom - centerY, 0, maxScrollTop);

    viewport.scrollLeft = nextScrollLeft;
    viewport.scrollTop = nextScrollTop;

    if (emit) {
      this.zoomChange.emit(this.currentZoom);
    }

    this.cdr.markForCheck();
  }

  private setupResizeObserver(): void {
    const viewport = this.viewportRef.nativeElement;
    this.resizeObserver = new ResizeObserver(() => {
      this.recomputeLayout();
    });
    this.resizeObserver.observe(viewport);
  }

  private getTouchDistance(a: Touch, b: Touch): number {
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.hypot(dx, dy);
  }

  private getTouchCenter(a: Touch, b: Touch): { x: number; y: number } {
    const viewport = this.viewportRef.nativeElement;
    const rect = viewport.getBoundingClientRect();
    return {
      x: (a.clientX + b.clientX) / 2 - rect.left,
      y: (a.clientY + b.clientY) / 2 - rect.top,
    };
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
  }

  private consumeDragClick(): boolean {
    if (!this.dragMoved) return false;
    this.dragMoved = false;
    return true;
  }

  private resetDragState(): void {
    this.dragPending = false;
    this.isDragging = false;
    this.dragMoved = false;
  }

  private updateInfoCardSize(): void {
    const el = this.infoCardRef?.nativeElement;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const width = Math.max(180, Math.ceil(rect.width));
    const height = Math.max(96, Math.ceil(rect.height));

    if (width !== this.infoCardWidth || height !== this.infoCardHeight) {
      this.infoCardWidth = width;
      this.infoCardHeight = height;
      queueMicrotask(() => this.cdr.detectChanges());
    }
  }

  private setDocked(value: boolean): void {
    if (!this.isDockedInfo) {
      if (this.forceDockedInfo) {
        this.forceDockedInfo = false;
        queueMicrotask(() => this.cdr.markForCheck());
      }
      return;
    }

    if (this.forceDockedInfo === value) return;
    this.forceDockedInfo = value;
    queueMicrotask(() => this.cdr.markForCheck());
  }

  private formatMetadataValue(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  private syncImageSize(): void {
    const image = this.imageRef?.nativeElement;
    if (!image || !image.complete || !image.naturalWidth) return;

    this.naturalSize = {
      width: image.naturalWidth,
      height: image.naturalHeight,
    };
    this.renderedBoxes = this.buildRenderedBoxes();
    this.recomputeLayout();
    this.imageLoaded.emit(this.naturalSize);
  }

  private recomputeLayout(): void {
    const viewport = this.viewportRef.nativeElement;
    if (!this.naturalSize || viewport.clientWidth === 0) return;

    const { width, height } = this.naturalSize;
    const availableWidth = viewport.clientWidth;
    const availableHeight = viewport.clientHeight;

    if (this.fitMode === 'height') {
      const resolvedHeight = availableHeight || (availableWidth * height) / width;
      this.baseHeight = resolvedHeight;
      this.baseWidth = (resolvedHeight * width) / height;
    } else if (this.fitMode === 'contain' || (this.fitMode === 'auto' && availableHeight > 0)) {
      const scale = Math.min(availableWidth / width, availableHeight / height);
      this.baseWidth = width * scale;
      this.baseHeight = height * scale;
    } else {
      this.baseWidth = availableWidth;
      this.baseHeight = (availableWidth * height) / width;
      if (availableHeight === 0) {
        viewport.style.height = `${this.baseHeight}px`;
      }
    }

    const shouldDock = viewport.clientWidth <= 640 || viewport.clientHeight <= 420;
    if (shouldDock !== this.isDockedInfo) {
      this.isDockedInfo = shouldDock;
      if (!shouldDock) {
        this.forceDockedInfo = false;
      }
    }

    this.updateStageSize();
  }

  private updateStageSize(): void {
    if (!this.baseWidth || !this.baseHeight) return;

    this.stageWidth = this.baseWidth * this.currentZoom;
    this.stageHeight = this.baseHeight * this.currentZoom;
    this.cdr.markForCheck();
  }

  private buildRenderedBoxes(): RenderedBox[] {
    const { width: imgWidth, height: imgHeight } = this.getImageSize();
    const xScale = imgWidth ? 100 / imgWidth : 1;
    const yScale = imgHeight ? 100 / imgHeight : 1;

    const normalized = this.boxes
      .filter((box) => box.width > 0 && box.height > 0)
      .map((box) => {
        const leftPct = this.coordinateSpace === 'normalized'
          ? box.x * 100
          : box.x * xScale;
        const topPct = this.coordinateSpace === 'normalized'
          ? box.y * 100
          : box.y * yScale;
        const widthPct = this.coordinateSpace === 'normalized'
          ? box.width * 100
          : box.width * xScale;
        const heightPct = this.coordinateSpace === 'normalized'
          ? box.height * 100
          : box.height * yScale;

        const area = widthPct * heightPct;

        return {
          ...box,
          leftPct,
          topPct,
          widthPct,
          heightPct,
          area,
          zIndex: 0,
        };
      })
      .sort((a, b) => b.area - a.area)
      .map((box, index) => ({
        ...box,
        zIndex: index + 1,
      }));

    return normalized;
  }

  private getImageSize(): ImageSize {
    if (this.coordinateSpace === 'normalized') {
      return { width: 1, height: 1 };
    }

    if (this.imageSize) {
      return this.imageSize;
    }

    if (this.naturalSize) {
      return this.naturalSize;
    }

    if (!this.warnedMissingImageSize) {
      this.warnedMissingImageSize = true;
      console.warn('[ImageVisualizer] imageSize is required when coordinateSpace="image". Falling back to normalized.');
    }

    return { width: 1, height: 1 };
  }

  private scrollFocusedIntoView(): void {
    if (this.selectedBoxId === null) return;

    const viewport = this.viewportRef.nativeElement;
    const boxId = typeof CSS !== 'undefined' && CSS.escape
      ? CSS.escape(String(this.selectedBoxId))
      : String(this.selectedBoxId);
    const target = viewport.querySelector<HTMLElement>(`[data-box-id="${boxId}"]`);
    if (!target) return;

    target.scrollIntoView({ block: 'center', inline: 'center' });
  }
}
